import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes } from "react-router-dom";

import Dashboard from "./views/Dashboard";
import Datenschutz from "./views/Datenschutz";
import Impressum from "./views/Impressum";
import PollDetail from "./views/PollDetail";
import Polls from "./views/Polls";
import Programs from "./views/Programs";
import PromiseDetail from "./views/PromiseDetail";
import Promises from "./views/Promises";
import Vorgaenge from "./views/Vorgaenge";

type Theme = "system" | "light" | "dark";

/** Erscheinungsbild: dem System folgen, aber umschaltbar. */
function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem("politwatch-theme");
      if (stored === "light" || stored === "dark") return stored;
    } catch {
      /* Speicher kann gesperrt sein (privates Fenster) */
    }
    return "system";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", theme);
    }
    try {
      if (theme === "system") localStorage.removeItem("politwatch-theme");
      else localStorage.setItem("politwatch-theme", theme);
    } catch {
      /* nicht kritisch */
    }
  }, [theme]);

  return { theme, setTheme };
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const next: Theme = theme === "dark" ? "light" : "dark";
  return (
    <button
      className="theme-toggle"
      onClick={() => setTheme(next)}
      title={next === "dark" ? "Dunkles Erscheinungsbild" : "Helles Erscheinungsbild"}
      aria-label={next === "dark" ? "Dunkles Erscheinungsbild" : "Helles Erscheinungsbild"}
    >
      {theme === "dark" ? "☾" : "☀"}
    </button>
  );
}

function NotFound() {
  return (
    <div className="empty">
      <p>Diese Seite gibt es nicht.</p>
      <Link to="/">Zur Übersicht</Link>
    </div>
  );
}

export default function App() {
  return (
    <div className="shell">
      <header className="topbar">
        <Link to="/" className="brand">
          Polit<span>Watch</span>
        </Link>
        <nav className="nav">
          <NavLink to="/" end>
            Übersicht
          </NavLink>
          <NavLink to="/versprechen">Versprechen</NavLink>
          <NavLink to="/abstimmungen">Abstimmungen</NavLink>
          <NavLink to="/vorgaenge">Vorgänge</NavLink>
          <NavLink to="/quellen">Quellen</NavLink>
        </nav>
        <ThemeToggle />
      </header>

      <main>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/versprechen" element={<Promises />} />
          <Route path="/versprechen/:id" element={<PromiseDetail />} />
          <Route path="/abstimmungen" element={<Polls />} />
          <Route path="/abstimmungen/:id" element={<PollDetail />} />
          <Route path="/vorgaenge" element={<Vorgaenge />} />
          <Route path="/quellen" element={<Programs />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* Die Quellenangabe "Deutscher Bundestag – DIP" ist keine Höflichkeit,
          sondern Auflage der DIP-Nutzungsbedingungen (Ziffer 4c). */}
      <footer>
        Quellen: Deutscher Bundestag – DIP · abgeordnetenwatch.de (CC0) ·
        Wahlprogramme der Parteien.{" "}
        <Link to="/quellen">Herkunft und Nutzungsrechte</Link>
        <br />
        <Link to="/impressum">Impressum</Link> · <Link to="/datenschutz">Datenschutz</Link>
        <br />
        Bewertungen sind Einschätzungen, keine Tatsachenbehauptungen; jede lässt sich
        über ihre Belege zum Originaldokument zurückverfolgen.
      </footer>
    </div>
  );
}
